/**
 * Eigenbau: Was ein Projekt verwaltet, statt in ihm zu arbeiten.
 *
 * **Warum es diesen Reiter gibt.** `projekte.archivieren` und
 * `projekte.wiederherstellen` liegen seit Paket 1 in der API und hatten bis
 * zum 14.08.2026 keinen Knopf — es gab in der ganzen Oberfläche keine Stelle,
 * an der ein Projekt verwaltet wird. Die übrigen Reiter sind Inhaltsansichten;
 * ein projektweites Archivieren zwischen Aufgaben und Meilensteine zu setzen,
 * hätte es zu einer Handlung neben der Arbeit gemacht statt über ihr.
 *
 * Kein neuer Baustein aus Tremor: Der Reiter besteht aus einer Datenliste und
 * einer Handlung mit Rückfrage. Beides ist im Entwurf vorhanden — `Rubrik`,
 * `Mikrolabel`, `Dialog` —, und ein eigener „Einstellungskarten"-Baustein für
 * einen einzigen Ort wäre Vorrat, kein Gewinn.
 *
 * **Was hier absichtlich nicht steht:** Name, Kürzel und Beschreibung sind
 * lesbar, aber nicht änderbar. `projekte.aendern` gibt es in der API und hat
 * ebenfalls keinen Knopf — das ist dieselbe Lücke, aber eine eigene Aufgabe.
 * Sie hier nebenbei mitzuerledigen hieße, eine Maske zu bauen, die niemand
 * bestellt hat.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { STANDARD_ZIEL_BRANCH, type Rolle } from "@cubic/shared";
import {
  ZEITSTRAHL_ZEITRAEUME,
  ZEITSTRAHL_ZEITRAUM_LABEL,
  ZEITSTRAHL_ZEITRAUM_SCHLUESSEL,
  istZeitstrahlZeitraum,
} from "@cubic/shared";
import { Button } from "@/components/Button";
import { Input } from "@/components/Input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/Dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Select";
import { Fehlerzeile } from "@/components/Fehlerzeile";
import { Rubrik } from "@/components/entwurf/Rubrik";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { api } from "@/lib/api";
import {
  fehlerMeldung,
  useEinstellungImProjekt,
  useEinstellungImProjektSetzen,
  useIch,
  useProjekt,
} from "@/lib/haken";
import { darfProjektArchivieren } from "@/lib/rollen";
import { cx, focusRing } from "@/lib/utils";
import { QualitaetsGateEinstellungen } from "./QualitaetsGateEinstellungen";

/**
 * Adressmuster der Review-Umgebungen.
 *
 * Der Wert liegt in der Einstellungs-Registry (`review_app_muster`, Bereich
 * Projekt) — kein eigenes Feld am Projekt: Die Lieferung liest ihn dort, und
 * die Rechteprüfung übernimmt der Einstellungsdienst beim Schreiben.
 */
function ReviewAppEinstellung({ projektId }: { projektId: string }) {
  const client = useQueryClient();
  const abfrageSchluessel = [
    "cubic",
    "einstellung",
    "review_app_muster",
    "projekt",
    projektId,
  ];
  const wert = useQuery({
    queryKey: abfrageSchluessel,
    queryFn: () =>
      api.einstellungen.lies.query({
        schluessel: "review_app_muster",
        bereich: "projekt",
        bereichId: projektId,
      }),
  });
  const [entwurf, setzeEntwurf] = useState<string | null>(null);
  const [meldung, setzeMeldung] = useState<
    ReturnType<typeof fehlerMeldung> | null
  >(null);
  const setzen = useMutation({
    mutationFn: (neu: string) =>
      api.einstellungen.setzen.mutate({
        schluessel: "review_app_muster",
        wert: neu,
        bereich: "projekt",
        bereichId: projektId,
      }),
    onSuccess: async (daten) => {
      setzeMeldung(null);
      setzeEntwurf(null);
      client.setQueryData(abfrageSchluessel, daten.wert);
      await client.invalidateQueries({ queryKey: abfrageSchluessel });
    },
    onError: (fehler) => setzeMeldung(fehlerMeldung(fehler)),
  });
  const angezeigt = entwurf ?? wert.data ?? "";

  return (
    <div className="mt-10">
      <Rubrik>Review-Apps</Rubrik>
      <p className="mt-4 max-w-[62ch] text-[13.5px] font-light leading-[1.65] text-tinte3">
        Adressmuster der Review-Umgebung eines offenen Merge Requests —{" "}
        <span className="font-mono">{"{iid}"}</span> wird durch die MR-Nummer
        ersetzt. Die Lieferung zeigt dann zu jedem offenen Merge Request den
        Verweis auf seine Umgebung. Leer bedeutet: keine Review-Apps.
      </p>
      <form
        className="mt-4 flex flex-wrap items-center gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          setzen.mutate(angezeigt.trim());
        }}
      >
        <Input
          value={angezeigt}
          onChange={(e) => setzeEntwurf(e.target.value)}
          placeholder="https://mr-{iid}-beispiel.review.example.dev"
          className="w-full max-w-[420px] font-mono text-[12.5px]"
          disabled={wert.isLoading}
          aria-label="Review-App-Adresse"
        />
        <Button
          type="submit"
          disabled={setzen.isPending || wert.isLoading || entwurf === null}
        >
          {setzen.isPending ? "Speichert …" : "Speichern"}
        </Button>
      </form>
      {meldung ? (
        <div className="mt-3">
          <Fehlerzeile meldung={meldung} />
        </div>
      ) : null}
    </div>
  );
}

/**
 * Hauptzweig des verknüpften Repositories.
 *
 * Anders als Review-App-Muster und Zeitband ist das **keine**
 * Anzeigeeinstellung: Der Wert bestimmt, von welchem Branch ein
 * Metarr-Code-Arbeitspaket ausgeht und wohin sein Merge Request zielt. Er
 * gehört deshalb als Spalte ans Projekt und nicht in die Einstellungs-Registry.
 *
 * Bis zum 23.08.2026 stand hier instanzweit `development`. Das galt für cubic
 * studio und für nichts sonst — `cubicmeta/cubic-time` heißt `develop`, und
 * dort endete jeder Start mit „404 Branch Not Found", bevor ein Modell lief.
 */
function ZielBranchEinstellung({ projektId }: { projektId: string }) {
  const client = useQueryClient();
  const projekt = useProjekt(projektId);
  const [entwurf, setzeEntwurf] = useState<string | null>(null);
  const [meldung, setzeMeldung] = useState<
    ReturnType<typeof fehlerMeldung> | null
  >(null);
  const gitlabProjekt = projekt.data?.gitlabProjekt ?? null;
  const gespeichert = projekt.data?.gitlabZielBranch ?? "";
  const angezeigt = entwurf ?? gespeichert;

  const setzen = useMutation({
    mutationFn: (neu: string) =>
      api.gitlab.setzeProjekt.mutate({
        projektId,
        /* Der Pfad bleibt, wie er ist — geändert wird nur der Zweig. */
        gitlabProjekt: gitlabProjekt ?? "",
        zielBranch: neu,
      }),
    onSuccess: async () => {
      setzeMeldung(null);
      setzeEntwurf(null);
      await Promise.all([
        projekt.refetch(),
        client.invalidateQueries({ queryKey: ["cubic", "metarr"] }),
      ]);
    },
    onError: (fehler) => setzeMeldung(fehlerMeldung(fehler)),
  });

  if (!gitlabProjekt) return null;

  return (
    <div className="mt-10">
      <Rubrik>Hauptzweig</Rubrik>
      <p className="mt-4 max-w-[62ch] text-[13.5px] font-light leading-[1.65] text-tinte3">
        Von diesem Branch geht ein Code-Arbeitspaket aus, und dorthin zielt sein
        Merge Request. Leer bedeutet{" "}
        <span className="font-mono">{STANDARD_ZIEL_BRANCH}</span> — passend für
        cubic studio selbst, aber nicht für jedes Repository.
      </p>
      <form
        className="mt-4 flex flex-wrap items-center gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          setzen.mutate(angezeigt.trim());
        }}
      >
        <Input
          value={angezeigt}
          onChange={(e) => setzeEntwurf(e.target.value)}
          placeholder={STANDARD_ZIEL_BRANCH}
          className="w-full max-w-[420px] font-mono text-[12.5px]"
          disabled={projekt.isLoading}
          maxLength={255}
          aria-label="Hauptzweig des Repositories"
        />
        <Button
          type="submit"
          disabled={setzen.isPending || projekt.isLoading || entwurf === null}
        >
          {setzen.isPending ? "Speichert …" : "Speichern"}
        </Button>
      </form>
      {meldung ? (
        <div className="mt-3">
          <Fehlerzeile meldung={meldung} />
        </div>
      ) : null}
    </div>
  );
}

/**
 * Ausschnitt des Meilenstein-Zeitbands.
 *
 * Auch dieser Wert liegt in der Einstellungs-Registry
 * (`zeitstrahl_zeitraum`, Bereich Projekt) und nicht als Spalte am Projekt:
 * Es ist eine Anzeigeeinstellung, die genau eine Ansicht liest — dafür eine
 * Migration zu schreiben hieße, das Schema zum Vorratslager zu machen.
 */
function ZeitstrahlEinstellung({ projektId }: { projektId: string }) {
  const wert = useEinstellungImProjekt(
    ZEITSTRAHL_ZEITRAUM_SCHLUESSEL,
    projektId,
  );
  const setzen = useEinstellungImProjektSetzen(
    ZEITSTRAHL_ZEITRAUM_SCHLUESSEL,
    projektId,
  );
  const gewaehlt = istZeitstrahlZeitraum(wert.data) ? wert.data : "automatisch";

  return (
    <div className="mt-10">
      <Rubrik>Meilensteinband</Rubrik>
      <p className="mt-4 max-w-[62ch] text-[13.5px] font-light leading-[1.65] text-tinte3">
        Welchen Zeitraum das Zeitband im Reiter Meilensteine zeigt.{" "}
        <span className="font-mono">Automatisch</span> spannt den Ausschnitt
        über die Fristen der aufgeplanten Aufgaben — er wächst und schrumpft
        also mit der Planung. Eine feste Spanne zeigt stattdessen ab dem
        laufenden Monat immer gleich viele Monate.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Select
          value={gewaehlt}
          onValueChange={(neu) => setzen.mutate(neu)}
          disabled={wert.isLoading || setzen.isPending}
        >
          <SelectTrigger
            aria-label="Zeitraum des Meilensteinbands"
            className="w-[280px]"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ZEITSTRAHL_ZEITRAEUME.map((eintrag) => (
              <SelectItem key={eintrag} value={eintrag}>
                {ZEITSTRAHL_ZEITRAUM_LABEL[eintrag]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {setzen.isPending ? (
          <Mikrolabel grad={11}>Speichert …</Mikrolabel>
        ) : null}
      </div>
      {setzen.error ? (
        <div className="mt-3">
          <Fehlerzeile meldung={fehlerMeldung(setzen.error)} />
        </div>
      ) : null}
    </div>
  );
}

export function EinstellungenReiter({
  projektId,
  rolle,
}: {
  projektId: string;
  rolle: Rolle | undefined;
}) {
  const projekt = useProjekt(projektId);
  const ich = useIch();
  const client = useQueryClient();
  const [archivOffen, setzeArchivOffen] = useState(false);
  const [meldung, setzeMeldung] = useState<
    ReturnType<typeof fehlerMeldung> | null
  >(null);

  const archiviert = Boolean(projekt.data?.archiviertAm);
  /*
   * Ein Projekt archiviert nur, wer es verwaltet. Serverseitig geprüft — der
   * Knopf verschwindet hier zusätzlich, damit niemand gegen eine Wand klickt.
   */
  const kann = darfProjektArchivieren(rolle, {
    rechte: ich.data?.rechte ?? [],
  });

  const archivieren = useMutation({
    mutationFn: () =>
      archiviert
        ? api.projekte.wiederherstellen.mutate({ id: projektId })
        : api.projekte.archivieren.mutate({ id: projektId }),
    onSuccess: async () => {
      setzeMeldung(null);
      setzeArchivOffen(false);
      await Promise.all([
        projekt.refetch(),
        client.invalidateQueries({ queryKey: ["cubic", "projekte"] }),
      ]);
    },
    onError: (fehler) => setzeMeldung(fehlerMeldung(fehler)),
  });

  if (projekt.isLoading) {
    return <Mikrolabel grad={12}>Lade Projekt …</Mikrolabel>;
  }
  if (!projekt.data) {
    return <Mikrolabel grad={12}>Projekt nicht erreichbar.</Mikrolabel>;
  }

  const felder: Array<[string, string]> = [
    ["Kürzel", projekt.data.kuerzel],
    ["Name", projekt.data.name],
    ["Beschreibung", projekt.data.beschreibung || "—"],
    ["Fassung", String(projekt.data.version)],
    [
      "Zustand",
      archiviert
        ? `archiviert am ${new Date(projekt.data.archiviertAm!).toLocaleDateString("de-DE")}`
        : "aktiv",
    ],
  ];

  return (
    <div className="max-w-[640px]">
      <Rubrik>Projekt</Rubrik>
      <dl className="mt-4">
        {felder.map(([label, wert]) => (
          <div
            key={label}
            className="flex items-baseline gap-6 border-b border-linie py-3"
          >
            <dt className="w-[124px] flex-none">
              <Mikrolabel grad={11.5}>{label}</Mikrolabel>
            </dt>
            <dd className="min-w-0 flex-1 text-[13.5px] text-tinte">{wert}</dd>
          </div>
        ))}
      </dl>
      <Mikrolabel grad={11} className="mt-3 block">
        Name, Kürzel und Beschreibung lassen sich hier noch nicht ändern —
        `projekte.aendern` gibt es in der API, eine Maske dafür noch nicht.
      </Mikrolabel>

      <QualitaetsGateEinstellungen projektId={projektId} rolle={rolle} />

      <ReviewAppEinstellung projektId={projektId} />

      <ZielBranchEinstellung projektId={projektId} />

      <ZeitstrahlEinstellung projektId={projektId} />

      <div className="mt-10">
        <Rubrik>Archiv</Rubrik>
        <p className="mt-4 max-w-[62ch] text-[13.5px] font-light leading-[1.65] text-tinte3">
          {archiviert
            ? "Das Projekt ist archiviert. Es verschwindet aus der Projektliste und der Schiene; Aufgaben, Seiten und der Verlauf bleiben unverändert erhalten."
            : "Ein archiviertes Projekt verschwindet aus der Projektliste und der Schiene. Nichts wird gelöscht, und Zurückholen geht jederzeit."}
        </p>

        {meldung ? (
          <div className="mt-4">
            <Fehlerzeile meldung={meldung} />
          </div>
        ) : null}

        {kann ? (
          <div className="mt-5">
            {archiviert ? (
              <button
                type="button"
                onClick={() => archivieren.mutate()}
                disabled={archivieren.isPending}
                className={cx(
                  "flex h-[31px] cursor-pointer items-center gap-2 px-[13px] text-[12.5px] shadow-[inset_0_0_0_1px_var(--tinte4)] transition-colors duration-150 ease-aus hover:bg-wisch disabled:opacity-40",
                  focusRing,
                )}
              >
                {archivieren.isPending ? "Holt zurück …" : "Wiederherstellen"}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setzeArchivOffen(true)}
                className={cx(
                  "flex h-[31px] cursor-pointer items-center gap-2 px-[13px] text-[12.5px] text-warn shadow-[inset_0_0_0_1px_var(--warn)] transition-colors duration-150 ease-aus hover:bg-warnflaeche",
                  focusRing,
                )}
              >
                Projekt archivieren
              </button>
            )}
          </div>
        ) : (
          <Mikrolabel grad={11.5} className="mt-5 block">
            Ein Projekt archiviert nur, wer es verwaltet.
          </Mikrolabel>
        )}
      </div>

      <Dialog open={archivOffen} onOpenChange={setzeArchivOffen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Projekt archivieren</DialogTitle>
          </DialogHeader>
          <p className="text-sm leading-relaxed">
            „{projekt.data.name}" verschwindet aus der Projektliste und aus der
            Schiene. Aufgaben, Seiten, Meilensteine und der Verlauf bleiben
            erhalten — zurückholen kannst du es hier jederzeit.
          </p>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setzeArchivOffen(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={() => archivieren.mutate()}
              disabled={archivieren.isPending}
            >
              {archivieren.isPending ? "Archiviert …" : "Archivieren"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
